import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { MOTIVO_BAJA_LABEL, type Empleado, type MotivoBaja, type Situacion } from '@/lib/personal';

/**
 * Dar de alta o dar de baja.
 *
 * Se avisa de lo que va a pasar además de cambiar el interruptor, porque son
 * tres cosas y ninguna es obvia: se cierra el contrato vigente, arranca el
 * plazo de conservación de los datos y se apaga a esa persona en la app de
 * lavandería, donde deja de salir en cuadrantes y no puede fichar.
 */
interface Props {
  abierto: boolean;
  empleado: Empleado;
  onCerrar: () => void;
  onGuardar: (datos: { situacion: Situacion; fecha: string; motivo?: string; nota?: string }) => Promise<void>;
}

const MOTIVOS = Object.keys(MOTIVO_BAJA_LABEL) as MotivoBaja[];

export default function ModalSituacion({ abierto, empleado, onCerrar, onGuardar }: Props) {
  const destino: Situacion = empleado.situacion === 'alta' ? 'baja' : 'alta';
  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });

  const [fecha, setFecha] = useState(hoy);
  const [motivo, setMotivo] = useState<MotivoBaja>('fin_contrato');
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contratoAbierto = empleado.contratos.find((c) => !c.fechaFin);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await onGuardar({
        situacion: destino,
        fecha,
        motivo: destino === 'baja' ? motivo : undefined,
        nota: nota.trim() || undefined,
      });
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title={destino === 'baja' ? 'Dar de baja' : 'Dar de alta'}>
      <form onSubmit={enviar} className="space-y-4">
        <label className="block">
          <span className="text-xs font-medium text-sereno-dark/60">
            Fecha de {destino === 'baja' ? 'baja' : 'incorporación'} *
          </span>
          <input type="date" required className="input mt-1" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </label>

        {destino === 'baja' && (
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Motivo *</span>
            <select className="input mt-1" value={motivo} onChange={(e) => setMotivo(e.target.value as MotivoBaja)}>
              {MOTIVOS.map((m) => (
                <option key={m} value={m}>{MOTIVO_BAJA_LABEL[m]}</option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="text-xs font-medium text-sereno-dark/60">Nota</span>
          <textarea className="input mt-1" rows={2} value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
          <AlertTriangle size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 space-y-1">
            <p className="font-medium">Qué va a pasar</p>
            <ul className="list-disc list-inside space-y-0.5">
              {destino === 'baja' ? (
                <>
                  {contratoAbierto && <li>Su contrato abierto se cerrará con fecha {fecha || '…'}.</li>}
                  <li>Se guardarán sus datos cinco años y después habrá que decidir si se borran.</li>
                  {empleado.lavanderiaId && (
                    <li>En la app de lavandería quedará inactiva: dejará de salir en los cuadrantes y no podrá fichar.</li>
                  )}
                </>
              ) : (
                <>
                  <li>Se borrará la fecha de baja y el plazo de conservación.</li>
                  <li>Su antigüedad no cambia: se conserva la fecha de alta original.</li>
                  {empleado.contratos.every((c) => c.fechaFin) && (
                    <li>Se queda sin contrato en vigor: acuérdate de añadir el nuevo.</li>
                  )}
                  {empleado.lavanderiaId && <li>En la app de lavandería volverá a estar activa.</li>}
                </>
              )}
            </ul>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCerrar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">
            {guardando ? 'Guardando…' : destino === 'baja' ? 'Dar de baja' : 'Dar de alta'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
